import * as React from 'react'
import { useState } from 'react'
import type { Vital, FaxJobSummary } from './api'
import './VitalsTimeline.css'

const PAGE_SIZE = 10

interface Props {
  vitals: Vital[]
  loading?: boolean
  faxJobs?: FaxJobSummary[]
}

interface VitalChip { label: string; value: string }

function getChips(v: Vital): VitalChip[] {
  const chips: VitalChip[] = []
  if (v.bp_systolic != null && v.bp_diastolic != null) {
    chips.push({ label: 'BP', value: `${v.bp_systolic}/${v.bp_diastolic} mmHg` })
  } else if (v.bp_systolic != null) {
    chips.push({ label: 'BP sys', value: `${v.bp_systolic} mmHg` })
  } else if (v.bp_diastolic != null) {
    chips.push({ label: 'BP dia', value: `${v.bp_diastolic} mmHg` })
  }
  if (v.blood_glucose != null)       chips.push({ label: 'Glucose',     value: `${v.blood_glucose} mg/dL` })
  if (v.temperature_f != null)       chips.push({ label: 'Temp',        value: `${v.temperature_f}°F` })
  if (v.heart_rate != null)          chips.push({ label: 'HR',          value: `${v.heart_rate} bpm` })
  if (v.respiratory_rate != null)    chips.push({ label: 'Resp',        value: `${v.respiratory_rate}/min` })
  if (v.height_in != null)           chips.push({ label: 'Height',      value: `${v.height_in} in` })
  if (v.weight_lb != null)           chips.push({ label: 'Weight',      value: `${v.weight_lb} lb` })
  if (v.spo2 != null)                chips.push({ label: 'SpO₂',        value: `${v.spo2}%` })
  if (v.pulse_rate != null)          chips.push({ label: 'Pulse',       value: `${v.pulse_rate} bpm` })
  if (v.bmi != null)                 chips.push({ label: 'BMI',         value: `${v.bmi}` })
  if (v.respiratory_volume != null)  chips.push({ label: 'Resp Vol',    value: `${v.respiratory_volume} L` })
  if (v.perfusion_index != null)     chips.push({ label: 'Perfusion',   value: `${v.perfusion_index}%` })
  if (v.peak_expiratory_flow != null) chips.push({ label: 'PEF',        value: `${v.peak_expiratory_flow} L/min` })
  if (v.force_expiratory_volume != null) chips.push({ label: 'FEV1',   value: `${v.force_expiratory_volume} L` })
  if (v.pain_score != null)          chips.push({ label: 'Pain',        value: `${v.pain_score}/10` })
  return chips
}

function formatWhen(iso: string): { date: string; time: string } {
  const d = new Date(iso)
  return {
    date: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    time: d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
  }
}

type FaxState = { status: FaxJobSummary['status']; label: string }

function buildFaxIndex(jobs: FaxJobSummary[]): Map<string, FaxState> {
  // For each vital_id, track the "best" fax job status:
  // sent > sending > queued > failed > permanently_failed > canceled
  const rank: Record<FaxJobSummary['status'], number> = {
    sent: 6, sending: 5, queued: 4, failed: 3, permanently_failed: 2, canceled: 1,
  }
  const map = new Map<string, FaxState>()
  for (const job of jobs) {
    for (const vid of job.vital_ids) {
      const existing = map.get(vid)
      if (!existing || rank[job.status] > rank[existing.status]) {
        map.set(vid, { status: job.status, label: job.recipient_name || 'Faxed' })
      }
    }
  }
  return map
}

function FaxBadge({ state }: { state: FaxState }): React.JSX.Element {
  const cls =
    state.status === 'sent' ? 'fax-badge fax-badge--sent' :
    state.status === 'sending' || state.status === 'queued' ? 'fax-badge fax-badge--pending' :
    'fax-badge fax-badge--failed'
  const icon =
    state.status === 'sent' ? '✓' :
    state.status === 'sending' || state.status === 'queued' ? '…' : '✕'
  const label =
    state.status === 'sent' ? 'Faxed' :
    state.status === 'sending' ? 'Sending' :
    state.status === 'queued' ? 'Queued' : 'Fax failed'
  return <span className={cls} title={state.label}>{icon} {label}</span>
}

export function VitalsTimeline({ vitals, loading, faxJobs }: Props): React.JSX.Element {
  const [page, setPage] = useState(1)

  // Reset to page 1 when resident changes (vitals array reference changes)
  React.useEffect(() => { setPage(1) }, [vitals])

  if (loading) {
    return <div className="timeline-empty">Loading history…</div>
  }
  if (vitals.length === 0) {
    return (
      <div className="timeline-empty">
        <p>No vitals recorded yet for this resident.</p>
      </div>
    )
  }

  const totalPages = Math.max(1, Math.ceil(vitals.length / PAGE_SIZE))
  const pageVitals = vitals.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const faxIndex = faxJobs ? buildFaxIndex(faxJobs) : new Map<string, FaxState>()

  return (
    <div>
      <ol className="timeline">
        {pageVitals.map((v, idx) => {
          const { date, time } = formatWhen(v.taken_at)
          const faxState = faxIndex.get(v.id)
          const isLast = idx === pageVitals.length - 1
          return (
            <li key={v.id} className={'timeline-item' + (isLast ? ' timeline-item-last' : '')}>
              <span className="timeline-dot" aria-hidden="true" />
              <div className="timeline-content">
                <div className="timeline-row">
                  <span className="timeline-date mono">{date}</span>
                  <span className="timeline-time mono muted">· {time}</span>
                  {faxState && <FaxBadge state={faxState} />}
                </div>
                {(() => {
                  const chips = getChips(v)
                  return chips.length > 0 ? (
                    <div className="timeline-chips">
                      {chips.map((c) => (
                        <span key={c.label} className="timeline-chip">
                          <span className="timeline-chip-label">{c.label}:</span>
                          <span className="timeline-chip-value">{c.value}</span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="timeline-summary muted">No readings recorded</p>
                  )
                })()}
                {v.notes ? (
                  <p className="timeline-notes">
                    <span className="timeline-notes-prefix">Note: </span>{v.notes}
                  </p>
                ) : null}
              </div>
            </li>
          )
        })}
      </ol>
      {totalPages > 1 && (
        <div className="timeline-pagination">
          <button
            className="timeline-page-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            ‹ Prev
          </button>
          <span className="timeline-page-info">{page} / {totalPages}</span>
          <button
            className="timeline-page-btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  )
}
